import { formatDate } from "../lib/format";
import { Check, Repeat, Bell, Trash2, Pencil, Archive } from "lucide-react";
import Tip from "./Tip";
import GoogleSyncBadge from "./GoogleSyncBadge";
import { EISENHOWER_META, eisenhowerKeyFromTask } from "../lib/eisenhower";

// Retro-compat: PRIORITY_META usato ancora da alcune pagine (Agenda/Deadlines
// raggruppamento "per priorità"). Bug 17/50 introduce la matrice Eisenhower
// come UI primaria (Costruisci/Gestisci/Risolvi) — vedi EISENHOWER_META.
export const PRIORITY_META = {
  critical: { label: "Critica", cls: "bg-red-100 text-red-800" },
  high: { label: "Alta", cls: "bg-orange-100 text-orange-800" },
  medium: { label: "Media", cls: "bg-blue-100 text-blue-800" },
  low: { label: "Bassa", cls: "bg-gray-100 text-gray-700" },
};

export const TYPE_LABEL = {
  task: "Ciclo", deadline: "Scadenza", payment_reminder: "Pagamento",
  urgency: "Urgenza", event: "Evento", habit: "Abitudine", note: "Nota",
  call: "Call", meeting: "Meeting", appointment: "Appuntamento", followup: "Follow-up",
};

export function countdownText(due) {
  if (!due) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d = new Date(due); d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - now) / (1000 * 60 * 60 * 24));
  if (diff === 0) return { txt: "oggi", cls: "text-red-700 font-semibold" };
  if (diff < 0) return { txt: `scaduto da ${Math.abs(diff)} gg`, cls: "text-red-700 font-semibold" };
  if (diff === 1) return { txt: "domani", cls: "text-orange-700 font-semibold" };
  if (diff <= 3) return { txt: `tra ${diff} gg`, cls: "text-orange-700" };
  if (diff <= 30) return { txt: `tra ${diff} gg`, cls: "text-blue-700" };
  return { txt: `tra ${diff} gg`, cls: "opacity-70" };
}

export default function TaskRow({
  task, area, onComplete, onEdit, onArchive, onDelete, onClick,
  scheduleAction,  // { label, icon: Component, onClick } → shows a prominent action button
  showArea = true, showPriority = true,
}) {
  const p = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  // Bug 17/50 · badge Eisenhower prende precedenza quando presente
  const eisKey = eisenhowerKeyFromTask(task);
  const eis = eisKey ? EISENHOWER_META[eisKey] : null;
  const cd = countdownText(task.due_date);
  const timeLabel = task.scheduled_time ? task.scheduled_time.slice(0, 5) : null;
  const ScheduleIcon = scheduleAction?.icon;
  const rowClickable = !!onClick;
  const handleRowClick = (e) => {
    if (!rowClickable) return;
    // Ignore clicks on interactive descendants
    const el = e.target;
    if (el.closest("button, a, input, [role='button']")) return;
    onClick?.(task);
  };
  return (
    <div
      className={`flex items-center gap-3 py-2.5 px-3 rounded-lg ${
        rowClickable ? "cursor-pointer hover:bg-blue-50/50" : "hover:bg-black/[0.02]"
      }`}
      data-testid="task-row"
      onClick={handleRowClick}
      role={rowClickable ? "button" : undefined}
      tabIndex={rowClickable ? 0 : undefined}
      onKeyDown={rowClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(task); } } : undefined}
    >
      <Tip label={task.status === "completed" ? "Riapri ciclo" : "Segna come completato"} side="right">
        <button onClick={(e) => { e.stopPropagation(); onComplete?.(task); }} className="w-5 h-5 rounded-md border-2 grid place-items-center hover:bg-green-50"
                style={{ borderColor: task.status === "completed" ? "#1F6B3A" : "var(--pxos-border-strong)" }}
                data-testid="task-complete-btn"
                aria-label={task.status === "completed" ? "Riapri ciclo" : "Segna come completato"}>
          {task.status === "completed" && <Check size={12} className="text-green-700"/>}
        </button>
      </Tip>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`font-medium ${task.status === "completed" ? "line-through opacity-50" : ""}`} data-testid="task-row-title">{task.title}</div>
          {task.recurrence_rule && <Repeat size={13} className="opacity-60" data-testid="task-recurring-icon" />}
          {task.reminder_offsets_days?.length > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] opacity-70" data-testid="task-reminders-count">
              <Bell size={11}/> {task.reminder_offsets_days.length}
            </span>
          )}
          <GoogleSyncBadge task={task} />
        </div>
        <div className="flex items-center gap-2 text-xs mt-0.5 flex-wrap" data-testid="task-row-meta">
          {task.scheduled_date && (
            <span className="opacity-80" data-testid="task-scheduled-info">
              Programmato per il {formatDate(task.scheduled_date)}
              {timeLabel ? ` alle ${timeLabel}` : " · senza orario"}
            </span>
          )}
          {task.due_date && (
            <span className={cd?.cls}>Scad. {formatDate(task.due_date)} · {cd?.txt}</span>
          )}
        </div>
      </div>
      {showArea && area && (
        <span className="hidden sm:inline-flex items-center gap-1.5 pxos-badge" data-testid="task-area-badge">
          <span className="w-2 h-2 rounded-full" style={{ background: area.color || "#B8B8B8" }}/>
          {area.name}
        </span>
      )}
      {showPriority && (eis
        ? <span className={`inline-flex text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${eis.cls}`} data-testid="task-eisenhower-badge">{eis.label}</span>
        : <span className={`inline-flex text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${p.cls}`} data-testid="task-priority-badge">{p.label}</span>
      )}
      <div className="flex items-center gap-1">
        {scheduleAction && (
          <Tip label={scheduleAction.label}>
            <button
              onClick={(e) => { e.stopPropagation(); scheduleAction.onClick?.(task); }}
              className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 text-blue-800 text-xs px-2.5 py-1 font-semibold hover:bg-blue-100"
              data-testid="task-schedule-btn"
              aria-label={scheduleAction.label}
            >
              {ScheduleIcon && <ScheduleIcon size={12}/>} {scheduleAction.label}
            </button>
          </Tip>
        )}
        <Tip label="Modifica il task"><button onClick={(e) => { e.stopPropagation(); onEdit?.(task); }} className="p-1.5 hover:bg-black/5 rounded opacity-70" data-testid="task-edit-btn" aria-label="Modifica task"><Pencil size={14}/></button></Tip>
        <Tip label="Archivia (nascondi dalle liste)"><button onClick={(e) => { e.stopPropagation(); onArchive?.(task); }} className="p-1.5 hover:bg-black/5 rounded opacity-70" data-testid="task-archive-btn" aria-label="Archivia task"><Archive size={14}/></button></Tip>
        <Tip label="Sposta nel cestino"><button onClick={(e) => { e.stopPropagation(); onDelete?.(task); }} className="p-1.5 hover:bg-black/5 rounded text-red-600 opacity-80" data-testid="task-delete-btn" aria-label="Sposta task nel cestino"><Trash2 size={14}/></button></Tip>
      </div>
    </div>
  );
}
